/**
 * Appearance control, and the bridge between shop cosmetics and the design
 * system.
 *
 * Two separate concerns share this module because they write to the same
 * place — the custom properties on `<html>`:
 *
 *   1. Appearance — dark / light / follow the OS. This is what makes the light
 *      theme in tokens.css reachable. Stored locally, applied before first
 *      paint of the app shell so there is no flash of the wrong surface.
 *   2. Cosmetic accents — the themes bought in the Shop. The legacy
 *      `applyTheme()` writes a fixed set of legacy variables (`--accent`,
 *      `--accent-light`, …) inline on `<html>`; components written against the
 *      design system read `--accent-base` and friends, so without a translation
 *      step a purchased cosmetic would recolour the monolith's inline styles
 *      and nothing else. `wrapGlobal` does that translation on every call.
 */

import { wrapGlobal, onRendered } from '../runtime/legacy.js';

const STORAGE_KEY = 'ironpact_appearance';
const MODES = ['dark', 'light', 'system'];
const DEFAULT_MODE = 'dark';

/** Surface each mode paints behind the page, for <meta name="theme-color">. */
const THEME_COLOR = { dark: '#0a0c10', light: '#f4f6f8' };

function readStored() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(stored) ? stored : DEFAULT_MODE;
  } catch {
    // Private mode / storage disabled — fall back rather than throw.
    return DEFAULT_MODE;
  }
}

function store(mode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* nothing to do */
  }
}

const prefersLight = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches;

/** The concrete theme a mode resolves to right now. */
function resolve(mode) {
  return mode === 'system' ? (prefersLight() ? 'light' : 'dark') : mode;
}

function apply(mode) {
  const resolved = resolve(mode);
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.dataset.appearance = mode;

  // The status bar on native and the browser chrome on Android read this, so a
  // stale value leaves a black bar above a light app.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[resolved]);

  document.querySelectorAll('[data-appearance-option]').forEach((el) => {
    const active = el.dataset.appearanceOption === mode;
    el.classList.toggle('active', active);
    el.setAttribute('aria-pressed', String(active));
  });
}

/* ─── Cosmetic accent bridge ──────────────────────────────────────────────── */

/** sRGB relative luminance, per WCAG 2.1. */
function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Parses `#rgb`, `#rrggbb` or `rgb()/rgba()` into [r, g, b], or null. */
function parseColor(value) {
  if (typeof value !== 'string') return null;
  const hex = value.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? [...hex[1]].map((c) => c + c).join('') : hex[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  }
  const fn = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  return fn ? fn.slice(1, 4).map(Number) : null;
}

const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Picks the label colour a filled button needs on this accent.
 *
 * The shop's accents were chosen for vibrancy, not legibility: white on Gold
 * Standard's #ffd700 is 1.4:1. Deriving the label from the fill means a
 * cosmetic can never ship an unreadable primary button.
 */
function inkFor(rgb) {
  const light = [255, 255, 255];
  const dark = [20, 16, 10];
  return contrast(rgb, dark) >= contrast(rgb, light) ? '#14100a' : '#ffffff';
}

const rgba = (rgb, alpha) => `rgba(${rgb.join(', ')}, ${alpha})`;

/** The custom properties this module writes inline when a cosmetic is active,
 *  and removes again when one is not. */
const COSMETIC_PROPS = [
  '--accent-base',
  '--accent-hover',
  '--accent-press',
  '--accent-rail',
  '--accent-tint',
  '--accent-tint-strong',
  '--accent-on',
  '--text-brand',
  '--text-link',
  '--focus-ring-color',
  '--viz-1',
];

/** The accent the stylesheet ships with. `applyTheme(null)` writes this same
 *  value inline, so it is how "no cosmetic equipped" is recognised. */
const DEFAULT_ACCENT = '#f97316';

/**
 * Re-derives the semantic accent tokens from whatever the legacy `applyTheme`
 * just wrote inline on `<html>`.
 *
 * Only writes when a cosmetic is actually equipped. Writing unconditionally
 * would pin the accent to an inline value, and an inline custom property beats
 * every stylesheet rule — including `:root[data-theme='light']`, which is how
 * the light theme swaps in the darker brand step it needs to clear 4.5:1 on
 * white. So the no-cosmetic path removes these properties rather than setting
 * them to a default.
 */
function syncCosmeticAccent() {
  const root = document.documentElement;
  const inline = root.style.getPropertyValue('--accent').trim();
  const equipped =
    inline &&
    inline.toLowerCase() !== DEFAULT_ACCENT &&
    globalThis.AppState?._activeThemeId != null;

  if (!equipped) {
    COSMETIC_PROPS.forEach((prop) => root.style.removeProperty(prop));
    return;
  }

  const base = parseColor(inline);
  if (!base) return;

  const computed = getComputedStyle(root);
  const hover = computed.getPropertyValue('--accent-light').trim() || inline;
  const press = computed.getPropertyValue('--accent-dark').trim() || inline;
  const rgb = `rgb(${base.join(', ')})`;

  root.style.setProperty('--accent-base', rgb);
  root.style.setProperty('--accent-hover', hover);
  root.style.setProperty('--accent-press', press);
  root.style.setProperty('--accent-rail', hover);
  root.style.setProperty('--accent-tint', rgba(base, 0.12));
  root.style.setProperty('--accent-tint-strong', rgba(base, 0.24));
  root.style.setProperty('--accent-on', inkFor(base));
  root.style.setProperty('--text-brand', hover);
  root.style.setProperty('--text-link', hover);
  root.style.setProperty('--focus-ring-color', hover);
  // Slot 1 of the categorical palette is the product's colour by definition,
  // so a cosmetic moves the primary series with it.
  root.style.setProperty('--viz-1', rgb);

  // The legacy variables also carry a background pair. Those are surfaces, and
  // a cosmetic that repaints the canvas fights the light theme, so they are
  // deliberately not forwarded — only the accent travels.
}

/* ─── Control ─────────────────────────────────────────────────────────────── */

function buildControl() {
  const wrap = document.createElement('div');
  wrap.className = 'appearance-toggle segmented';
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', 'Appearance');

  const icons = {
    dark: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>',
    light: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>',
    system: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="4" width="20" height="13" rx="2"/><path d="M8 21h8M12 17v4"/></svg>',
  };
  const labels = { dark: 'Dark', light: 'Light', system: 'System' };

  MODES.forEach((mode) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.appearanceOption = mode;
    btn.title = `${labels[mode]} appearance`;
    btn.setAttribute('aria-label', `${labels[mode]} appearance`);
    btn.innerHTML = icons[mode];
    btn.addEventListener('click', () => setMode(mode));
    wrap.appendChild(btn);
  });
  return wrap;
}

function setMode(mode) {
  store(mode);
  apply(mode);
  syncCosmeticAccent();
}

export function installTheme() {
  apply(readStored());

  // Follow the OS only while the user has actually asked us to.
  if (typeof matchMedia === 'function') {
    matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
      if (readStored() === 'system') apply('system');
    });
  }

  // The cosmetic bridge. `applyTheme` runs on load and on every equip/unequip.
  wrapGlobal('applyTheme', (next, ...args) => {
    const result = next(...args);
    syncCosmeticAccent();
    return result;
  });
  syncCosmeticAccent();

  // The sidebar and mobile header are re-rendered by the monolith, so the
  // control is attached per rendered element rather than once at startup.
  onRendered('.sidebar-footer', (footer) => {
    if (footer.querySelector('.appearance-toggle')) return;
    const control = buildControl();
    footer.appendChild(control);
    apply(readStored());
  });
}
